import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormArray } from '@angular/forms';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { PrTableFormComponent } from '@app/components/pr-form/pr-table-form/pr-table-form.component';
import { PrTableColumn } from '@app/components/pr-table/pr-table.component';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { PledgeRead } from '@app/forms/subscription/definition/interface/Pledge';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { EaScpiTableFormBuilder } from './ea-simulation-scpi-pack-form.builder';

@UntilDestroy()
@Component({
    selector: 'scpi-table-editable',
    templateUrl: './scpi-table-editable.component.html',
    styleUrls: ['./scpi-table-editable.component.scss'],
    standalone: false
})
export class ScpiTableEditableComponent
  extends EaScpiTableFormBuilder
  implements AfterViewInit, OnInit
{
  scpiList?;
  clientRiskScore: number;
  pledge: PledgeRead;
  doneSubject: Subject<void> = new Subject<void>();

  @ViewChild('tableForm', { static: true }) tableForm: PrTableFormComponent;

  constructor(
    protected formConstructor: PrFormConstructor,
    private referenceDao: ReferenceDao,
    private subscriptionFacade: SubscriptionFacadeService,
    private cdr: ChangeDetectorRef,
  ) {
    super(formConstructor);
    this.initForm([], this.options);
    this.pledge = this.subscriptionFacade.getPledge();
    this.clientRiskScore = this.pledge?.owners?.[0]?.riskProfil?.score;
    this.referenceDao
      .getScpi()
      .pipe(untilDestroyed(this))
      .subscribe(scpi => (this.scpiList = scpi));
  }

  percentColumnsDefinition = [
    new PrTableColumn('Noms des SCPI', 'productName', { span: 3 }),
    new PrTableColumn('Repartition', 'repartition', 'input', {
      changeCallback: this.tableAllocationColumnValueChanged.bind(this),
      suffix: '%',
      span: 2,
      hasTotal: true,
    }),
    new PrTableColumn('Parts', 'scpiNbPart', 'text', {
      span: 2,
      hasTotal: true,
    }),
    new PrTableColumn('Montant investi', 'scpiInvestmentPrice', 'currency', {
      span: 2,
      hasTotal: true,
    }),
    new PrTableColumn('', 'action', 'action'),
  ];

  shareColumnsDefinition = [
    new PrTableColumn('Noms des SCPI', 'productName', { span: 3 }),
    new PrTableColumn('Repartition', 'repartition', 'percent-number', {
      value: val => val.repartition.toFixed(3),

      span: 2,
      hasTotal: true,
    }),
    new PrTableColumn('Parts', 'scpiNbPart', 'input', {
      changeCallback: this.tablePartPriceColumnValueChanged.bind(this),
      span: 2,
      hasTotal: true,
    }),
    new PrTableColumn('Montant investi', 'scpiInvestmentPrice', 'currency', {
      span: 2,
      hasTotal: true,
    }),
    new PrTableColumn('', 'action', 'action'),
  ];

  currentColumnDefinition = this.percentColumnsDefinition;
  ngOnInit(): void {
    const pledge = this.subscriptionFacade.getPledge();

    this.initForm(pledge.products || {}, this.options);
    this.cdr.detectChanges();
  }
  ngAfterViewInit(): void {
    this.cdr.detectChanges();
    if (this.options.ownership == 'BARE_OWNERSHIP') {
      this.percentColumnsDefinition.splice(
        3,
        0,
        new PrTableColumn(
          'Clé de démembrement',
          'dismembermentDistributionKey',
          'input',
          {
            changeCallback:
              this.tableDismembermentColumnValueChanged.bind(this),
            suffix: '%',
            span: 2,
          },
        ),
      );
      this.shareColumnsDefinition.splice(
        3,
        0,
        new PrTableColumn(
          'Clé de démembrement',
          'dismembermentDistributionKey',
          'input',
          {
            changeCallback:
              this.tableDismembermentColumnValueChanged.bind(this),
            suffix: '%',
            span: 2,
          },
        ),
      );
    }
    this.currentColumnDefinition = this.shareColumnsDefinition;
  }

  tableAllocationColumnValueChanged(value: any, row: any, column: any) {
    this.scpis.controls[row.index].setValue({
      ...this.scpis.controls[row.index].value,
      repartition: (value / 100)?.toFixed(3),
      parameters: {
        ...this.scpis.controls[row.index].value.parameters,
        scpiNbPart: row?.parameters?.scpiPartPrice
          ? Math.floor(
              ((value / 100) *
                //this.options.scpiInvestmentPrice)
                _.sumBy(
                  [...this.options],
                  product => product?.parameters?.scpiInvestmentPrice,
                )) /
                (row?.parameters?.scpiPartPrice *
                  (row?.parameters?.dismembermentDistributionKey
                    ? row?.parameters?.dismembermentDistributionKey
                    : 1)),
            )
          : null,
      },
    });
    this.markAsDirtyTouchedAndRefresh(row.index);
  }

  tablePartPriceColumnValueChanged(value: any, row: any, column: any) {
    let scpi = this.scpis.controls[row.index];
    scpi.patchValue({ parameters: { scpiNbPart: +value } });
    this.scpis?.controls?.map(scpi =>
      scpi.patchValue({
        parameters: {
          scpiInvestmentPrice:
            scpi.value.parameters.scpiNbPart *
            scpi.value.parameters.scpiPartPrice *
            (scpi.value.parameters.dismembermentDistributionKey || 1),
        },
      }),
    );

    let totalscpiInvestmentPrice = this.scpis.controls.reduce(
      (acc, scpi) => (acc += scpi.value?.parameters?.scpiInvestmentPrice),
      0,
    );
    this.scpis.controls.map(scpi =>
      scpi.patchValue({
        repartition:
          scpi.value?.parameters?.scpiInvestmentPrice / totalscpiInvestmentPrice
            ? (
                scpi.value?.parameters?.scpiInvestmentPrice /
                totalscpiInvestmentPrice
              )?.toFixed(3)
            : scpi.value?.parameters?.scpiInvestmentPrice /
              totalscpiInvestmentPrice,
      }),
    );

    this.options.scpiInvestmentPrice = totalscpiInvestmentPrice;

    this.markAsDirtyTouchedAndRefresh(row.index);
  }

  tableDismembermentColumnValueChanged(value: any, row: any, column: any) {
    this.scpis.controls[row.index].setValue({
      ...this.scpis.controls[row.index].value,
      paramters: {
        ...this.scpis.controls[row.index].value.parameters,
        [column.name]: value / 100,
      },
    });
    this.markAsDirtyTouchedAndRefresh(row.index);
  }

  markAsDirtyTouchedAndRefresh(control) {
    this.scpis.controls[control].markAsDirty();
    this.scpis.controls[control].markAsTouched();
    this.tableForm.initTable();
  }

  formatTableDataFunction() {
    return data =>
      _(data)
        .map((data, i) => {
          return {
            index: i,
            productName: data.productName,
            repartition: (data.repartition * 100)?.toFixed(3),
            scpiNbPart: data?.parameters?.scpiNbPart,
            dismembermentDistributionKey:
              data?.parameters?.dismembermentDistributionKey * 100,
            scpiInvestmentPrice:
              data?.parameters?.scpiPartPrice *
              (data?.parameters?.dismembermentDistributionKey || 1) *
              data?.parameters?.scpiNbPart,
            scpiPartPrice: data?.parameters?.scpiPartPrice,
          };
        })
        .value();
  }

  newOptionLogic() {
    console.log('new option');
  }

  onSubmit() {
    this.derrors = true;

    // stop here if form is invalid
    if (this.form.invalid || this.totalAllocatedPercent != 1) {
      return;
    }

    this.doneSubject.next();
    this.form.markAsDirty();

    this.subscriptionFacade.submitPledge(this, 'products');
  }
  prev() {
    this.subscriptionFacade.previousStep();
  }

  toggleColumnType(event) {
    this.currentColumnDefinition =
      event.id == 'PERCENT'
        ? this.percentColumnsDefinition
        : this.shareColumnsDefinition;
  }

  floored(value) {
    return value ? Math.round(value * 100) / 100 : 0;
  }

  get scpis() {
    return this.form.get('scpis') as UntypedFormArray;
  }

  get totalAllocatedPercent() {
    return this.floored(
      _(this.scpis.value)
        .map(item => Number(item?.repartition))
        .sum(),
    );
  }

  get allocationPanel() {
    if (!this.scpis || !this.scpis.value || this.scpis.value.length == 0)
      return {
        display: 'info',
        icon: 'info',
        text: 'Renseignez vos investissements',
      };
    else if (this.totalAllocatedPercent > 1)
      return {
        display: 'danger',
        icon: 'error',
        text: `${Math.round(
          this.totalAllocatedPercent * 100,
        )}% au total - Vous ne pouvez pas repartir plus de 100% de l'investissement`,
      };
    else if (this.totalAllocatedPercent < 1) {
      return {
        display: 'warning',
        icon: 'warning',
        text: `Il reste ${Math.round(
          //this.options.scpiInvestmentPrice
          _.sumBy(
            [...this.options],
            product => product?.parameters?.scpiInvestmentPrice,
          ) *
            (1 - this.totalAllocatedPercent),
        )}€ de votre investissement à répartir, soit ${Math.round(
          (1 - this.totalAllocatedPercent) * 100,
        )}%`,
      };
    }
    // else if (_.filter(this.scpis.value, (scpi) => !scpi.repartition).length)
    //   return {
    //     display: "danger",
    //     icon: "error",
    //     text: "Vous ne pouvez pas repartir 0% dans un support",
    //   };
    else if (this.totalAllocatedPercent == 1)
      return {
        display: 'success',
        icon: 'check',
        text: 'Félicitations, vos investissements sont répartis à 100%',
      };
  }
  setQuestionStep() {
    const q = this.subscriptionFacade
      ?.getQuestions()
      ?.findIndex(q => q.name === 'productForm');
    if ((q || q == 0) && q !== -1) {
      this.subscriptionFacade.setStep(Number(q) + 1);
    }
  }

  get riskNote() {
    // if we don't have the client risk score, we can't calculate anything
    if (this.clientRiskScore == null) {
      return null;
    }
    //if allocation is not complete, we can't calculate anything
    if (this.totalAllocatedPercent != 1) {
      return null;
    }

    const data = this.scpis?.value;
    // Initialize the total risk contribution
    let riskContrat = 0;

    // Iterate over the array and calculate the contribution for each item
    for (let item of data) {
      // if at least one risk score is null, we can't calculate anything
      if (item?.parameters?.srri == null) {
        return null;
      }

      const repartition = item?.repartition
        ? item?.repartition
        : 1 / data?.length;

      riskContrat += item.parameters.srri * repartition;
    }

    if (riskContrat < this.clientRiskScore - 0.75) {
      return {
        display: 'warning',
        icon: 'warning',
        text:
          `L’allocation que vous avez choisie présente un couple rendement/risque plus faible que le profil de risque que vous avez défini.
        (niveau de risque du contrat de ` +
          riskContrat?.toFixed(1) +
          ` pour un profil de risque de ` +
          this.clientRiskScore?.toFixed(1) +
          `)`,
      };
    }
    if (riskContrat > this.clientRiskScore) {
      return {
        display: 'warning',
        icon: 'warning',
        text:
          `L’allocation que vous avez choisie présente une note de risque supérieure à votre profil de risque. Votre allocation peut engendrer un rendement important mais aussi une exposition plus élevée aux fluctuations du marché, tant à la hausse qu’à la baisse.
        (niveau de risque du contrat de ` +
          riskContrat?.toFixed(1) +
          ` pour un profil de risque de ` +
          this.clientRiskScore?.toFixed(1) +
          `)`,
      };
    }
    return null;
  }
}
